import React from 'react';
import { autoId } from '@workday/upc-common';
import { ResendOTP } from '../resend-otp/resend-otp';
import { ID_OTP_VERIFY_BUTTON } from '../../autoIds';
import { PrimaryButton, Flex } from '@workday/canvas-kit-react';
import styles from './OTPVerificationForm.module.scss';
import { useTranslation } from 'react-i18next';
import { PasscodeFormState, OTPFormField } from '../otp-form-field/otp-form-field';
import { useForm } from 'react-hook-form';
import { useErrorHandling } from '../../hooks';
import { serverError } from '../../utils/server-error';
import { UseMutationStateResult } from '@reduxjs/toolkit/dist/query/react/buildHooks';

interface OTPVerificationFormProps {
    errorPath?: string;
    onResend: () => void;
    onSubmit: ({ passcode }) => void;
    disabled?: boolean;
    otpContact: any;
    mutation: UseMutationStateResult<any, any>;
    alternativeContactLinkProps?: any;
}

export function OTPVerificationForm({
    errorPath = '',
    onResend,
    onSubmit,
    otpContact,
    disabled,
    mutation
}: OTPVerificationFormProps) {
    const { control, handleSubmit, setError, reset } = useForm<PasscodeFormState>({
        defaultValues: {
            passcode: ''
        }
    });

    useErrorHandling(mutation.error, {
        inlineErrors: ['UPC-1005', 'UPC-1006', 'UPC-1017', 'UPC-1054'],
        onInlineError: error => setError('passcode', serverError(error))
    });

    const { t } = useTranslation();

    function onResendHandler() {
        reset();
        onResend();
    }

    function onSubmitHandler(values) {
        onSubmit({ passcode: values.passcode });
    }

    return (
        <>
            <h1 className={styles.title}>{t('OTPVerificationForm.enterYourCode')}</h1>
            <p className={styles.body}>
                {otpContact.contactType === 'email'
                    ? t('OTPVerificationForm.verifyEmail')
                    : t('OTPVerificationForm.verifyPhone')}
            </p>
            <p className={styles.email}>{otpContact.displayName}</p>
            <form className={styles.form} onSubmit={handleSubmit(onSubmitHandler)}>
                <OTPFormField
                    disabled={disabled}
                    autoFocus={true}
                    control={control}
                    className={styles.otpVerification}
                />
                <PrimaryButton
                    {...autoId(ID_OTP_VERIFY_BUTTON)}
                    disabled={disabled}
                    size='large'
                    grow={true}
                    type='submit'
                    marginBottom='1rem'>
                    {t('OTPVerificationForm.submitButton')}
                </PrimaryButton>
                <Flex flexDirection='column'>
                    <ResendOTP
                        errorPath={errorPath}
                        isDisabled={disabled}
                        onResend={onResendHandler}
                        otpType={otpContact.contactType}
                    />
                </Flex>
            </form>
        </>
    );
}
